import { useEffect, useId, useRef, useState } from 'preact/hooks';
import { FieldHeader } from './FieldHeader';
import { AppIcon } from './AppIcon';
import SignaturePadLib, { type PointGroup } from 'signature_pad';
import type { SignatureInput } from '../lib/pdf';
import { PDF_SIGNATURE_LIMITS, SIGNATURE_LINE } from '../../shared/pdf/signatures';

/** The strokes behind each drawn signature this page has made, so a pad shown again puts every stroke back exactly
 * where it was drawn. The pad is a plain canvas: strokes are never moved, scaled or recentered. */
const strokes = new Map<string, PointGroup[]>();
const keyOf = (png: Uint8Array) => btoa(png.reduce((s, byte) => s + String.fromCharCode(byte), ''));
// A pen half again as broad as before: the signature is cropped to its ink and shrunk to fit its box on the form, often to a fifth of its size
// or less, and a thin line would come out a hairline there.
const PEN = { penColor: '#684b73', minWidth: 1.2, maxWidth: 3.3 };

interface Props { label: string;
  /** Who signs, such as the participant's name: shown in place of the label, on a card that already says whose signature it is. */ signer?: string; optional?: boolean; value: SignatureInput | null; onChange: (v: SignatureInput | null) => void; typedDefault?: string; error?: string }

export function SignaturePad({ label, signer, optional, value, onChange, typedDefault, error }: Props) {
  const errorId = useId();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePadLib | null>(null);
  // A signature from before a reload, shown as its image since its strokes are gone; cleared with the pad.
  const restoredRef = useRef<HTMLImageElement | undefined>(undefined);
  // The pad's stroke listener is set up once per mode, so it calls the latest onChange through this, never a stale one.
  const latestChange = useRef(onChange); latestChange.current = onChange;
  const [mode, setMode] = useState<'draw' | 'type'>(value?.kind === 'typed' ? 'type' : 'draw');
  const [typed, setTyped] = useState(value?.kind === 'typed' ? value.name : typedDefault ?? '');
  const [fullScreen, setFullScreen] = useState(false);
  // Counts signatures signed full screen, so the pad sets itself up again to show the new one.
  const [signedFullScreen, setSignedFullScreen] = useState(0);

  useEffect(() => {
    if (mode !== 'draw') return;
    const canvas = canvasRef.current!;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    restoredRef.current = undefined;
    // The restored image sits in the middle, its line on the pad's, shrunk to fit if it must.
    const showRestored = () => {
      const image = restoredRef.current; if (!image) return;
      const line = canvas.offsetHeight * SIGNATURE_LINE;
      const scale = Math.min((canvas.offsetWidth - 24) / image.width, (line - 12) / (image.height * SIGNATURE_LINE), 1);
      const width = image.width * scale, height = image.height * scale;
      canvas.getContext('2d')!.drawImage(image, (canvas.offsetWidth - width) / 2, line - height * SIGNATURE_LINE, width, height);
    };
    const resize = () => {
      // A hidden pad (in a closed section) has no size; resizing it would erase the drawing, so wait until it shows.
      if (!canvas.offsetWidth || !canvas.offsetHeight) return;
      const drawn = padRef.current?.toData() ?? [];
      canvas.width = canvas.offsetWidth * ratio; canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d')!.scale(ratio, ratio);
      padRef.current?.clear();
      // Resizing empties the canvas, so a restored signature is drawn again, under any strokes added since.
      showRestored();
      if (drawn.length) padRef.current?.fromData(drawn, { clear: false });
    };
    const pad = new SignaturePadLib(canvas, PEN);
    padRef.current = pad;
    resize();
    if (value?.kind === 'drawn') {
      const key = keyOf(value.png), drawn = strokes.get(key);
      if (drawn) pad.fromData(drawn);
      else {
        // A signature from before a reload, or signed full screen, has no strokes here, only its image: show that.
        const image = new Image();
        image.onload = () => {
          if (padRef.current !== pad) return;
          restoredRef.current = image; showRestored();
        };
        image.src = `data:image/png;base64,${key}`;
      }
    }
    window.addEventListener('resize', resize);
    pad.addEventListener('endStroke', () => {
      const png = trimmedPng(canvas);
      if (strokes.size > 50) strokes.clear();
      strokes.set(keyOf(png), pad.toData());
      latestChange.current({ kind: 'drawn', png });
    });
    return () => { window.removeEventListener('resize', resize); pad.off(); padRef.current = null; };
  }, [mode, signedFullScreen]);

  const clear = () => { restoredRef.current = undefined; padRef.current?.clear(); onChange(null); };

  return (
    <div class="sig" role="group" aria-label={label}>
      {/* On a signer's card, which already says whose signature it is, only the signer's name shows; the label still names
        the pad for screen readers. */}
      {/* With no name yet, such as in a preview, the heading says whose signature it is rather than being left empty. */}
      {signer !== undefined ? <h3 class="sig-signer">{signer.trim() || label}</h3> : <span class="label">{label}{optional && <span class="optional-label">Optional</span>}</span>}
      {/* The error sits on the Draw and Type row, at the right. */}
      <FieldHeader labelClass="row small sig-modes" errorId={errorId} error={error} label={<>
        <button type="button" class={mode === 'draw' ? 'link active' : 'link'} onClick={() => { if (mode !== 'draw') { setMode('draw'); onChange(null); } }}>Draw</button>
        <button type="button" class={mode === 'type' ? 'link active' : 'link'} onClick={() => { if (mode !== 'type') { setMode('type'); onChange(null); } }}>Type</button>
      </>} />
      {mode === 'draw' ? (
        // A phone held upright is too narrow to sign on comfortably, so there the pad asks to be turned sideways (see the
        // stylesheet), keeping a signature already made in view. Full screen works with the screen's rotation locked.
        <div class={`sig-draw${value ? ' signed' : ''}`}>
          <div class="sig-pad">
            <canvas ref={canvasRef} class="sig-canvas" tabIndex={0} aria-invalid={!!error} aria-describedby={error ? errorId : undefined} aria-label={`${label} – draw with your finger or mouse`} />
            {/* The line to sign on, which goes on the form's signature line. */}
            <span class="sig-line" aria-hidden="true" />
          </div>
          <div class="sig-rotate"><AppIcon name="rotate" />{/* Full screen works with the screen's rotation locked, when turning the phone does nothing. */}
            <p>Turn your phone sideways or tap <strong>Sign full screen</strong> to sign.</p>
            <button type="button" class="secondary" onClick={() => setFullScreen(true)}><AppIcon name="expand" /> Sign full screen</button></div>
          <div class="row between small sig-row"><span class="muted sig-hint">Sign on the line with your finger or mouse.</span><span class="muted sig-hint-turn">Turn your phone sideways or use Full screen to sign again.</span><span class="row sig-actions">
            <button type="button" class="link sig-fullscreen-open" onClick={() => setFullScreen(true)}><AppIcon name="expand" /> Full screen</button>
            <button type="button" class="link" onClick={clear}>Clear</button></span></div>
          {fullScreen && <FullScreenPad label={label} signer={signer} onClose={() => setFullScreen(false)}
            onDone={png => { restoredRef.current = undefined; setFullScreen(false); setSignedFullScreen(n => n + 1); onChange({ kind: 'drawn', png }); }} />}
        </div>
      ) : (
        <>
          <input aria-label={`${label} – full name`} aria-invalid={!!error} aria-describedby={error ? errorId : undefined} value={typed} placeholder="Type your full name" onInput={(e) => { const t = (e.target as HTMLInputElement).value; setTyped(t); onChange(null); }} />
          <button type="button" disabled={!typed.trim() || !!value} class="secondary" onClick={() => onChange(typed.trim() ? { kind: 'typed', name: typed.trim() } : null)}>{value ? 'Signature confirmed' : 'Use this signature'}</button>
          <div class="sig-typed">{typed || ' '}</div>
          <span class="muted small">{value ? 'Signature confirmed. Edit your name to change it.' : 'Select “Use this signature” to confirm your name as your electronic signature.'}</span>
        </>
      )}
      {value && <p class="small reg-signed" role="status">Signature added</p>}
    </div>
  );
}

/** A pad over the whole screen, for a phone: sideways, several times as wide as the pad on the page. Done hands back the signature,
 * which replaces any on the pad.
 *
 * A page can't lock the screen's orientation on an iPhone, so on a phone held upright the bar and the line to sign on are laid out
 * sideways, for the signer to turn the phone. The canvas itself stays upright, so touches land where they're drawn; the signature
 * is turned to read sideways when it's handed back. */
function FullScreenPad({ label, signer, onDone, onClose }: { label: string; signer?: string; onDone: (png: Uint8Array) => void; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePadLib | null>(null);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    dialog.current!.showModal();
    // The page underneath stays put while the signer draws.
    const page = document.documentElement, overflow = page.style.overflow;
    page.style.overflow = 'hidden';
    const canvas = canvasRef.current!;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    // A broader pen still: a signature this big is shrunk further to fit the form.
    const pad = new SignaturePadLib(canvas, { ...PEN, minWidth: PEN.minWidth * 1.5, maxWidth: PEN.maxWidth * 1.5 });
    padRef.current = pad;
    let wasSideways = sideways(), width = canvas.offsetWidth;
    const resize = () => {
      if (!canvas.offsetWidth || !canvas.offsetHeight) return;
      // When the browser turns with the phone, strokes turn with it, so they still read the same way to the signer.
      const was = width, now = canvas.offsetWidth;
      const turn = sideways() === wasSideways ? undefined : wasSideways
        ? (p: { x: number; y: number }) => ({ x: p.y, y: was - p.x })
        : (p: { x: number; y: number }) => ({ x: now - p.y, y: p.x });
      wasSideways = sideways(); width = now;
      const drawn = turn ? pad.toData().map(group => ({ ...group, points: group.points.map(p => ({ ...p, ...turn(p) })) })) : pad.toData();
      // Turning the phone changes the pad's shape; strokes that would no longer fit shrink, toward the top left, until they do.
      const points = drawn.flatMap(group => group.points);
      const scale = Math.min(1, ...points.map(p => Math.min((canvas.offsetWidth - 12) / Math.max(p.x, 1), (canvas.offsetHeight - 12) / Math.max(p.y, 1))));
      canvas.width = canvas.offsetWidth * ratio; canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d')!.scale(ratio, ratio);
      pad.clear();
      if (drawn.length) pad.fromData(scale < 1 ? drawn.map(group => ({ ...group, points: group.points.map(p => ({ ...p, x: p.x * scale, y: p.y * scale })) })) : drawn, { clear: false });
    };
    resize();
    window.addEventListener('resize', resize);
    pad.addEventListener('endStroke', () => setEmpty(pad.isEmpty()));
    return () => { window.removeEventListener('resize', resize); pad.off(); padRef.current = null; page.style.overflow = overflow; };
  }, []);

  const clear = () => { padRef.current?.clear(); setEmpty(true); };
  return <dialog class="sig-fullscreen" ref={dialog} aria-label={label} onCancel={event => { event.preventDefault(); onClose(); }}>
    <div class="sig-fullscreen-bar">
      <button type="button" class="link" onClick={onClose}>Cancel</button>
      <strong>{signer?.trim() || label}</strong>
      <span class="row sig-actions"><button type="button" class="link" disabled={empty} onClick={clear}>Clear</button>
        <button type="button" disabled={empty} onClick={() => onDone(trimmedPng(sideways() ? turnedToRead(canvasRef.current!) : canvasRef.current!))}>Done</button></span>
    </div>
    <div class="sig-fullscreen-area">
      <canvas ref={canvasRef} tabIndex={0} aria-label={`${label} – draw with your finger`} />
      <span class="sig-fullscreen-line" aria-hidden="true" />
    </div>
  </dialog>;
}

/** Whether the full-screen pad is laid out sideways on an upright screen. */
const sideways = () => matchMedia('(orientation: portrait)').matches;

/** A copy of an upright canvas signed sideways, turned a quarter turn back so the signature reads left to right. */
function turnedToRead(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const out = document.createElement('canvas');
  out.width = canvas.height; out.height = canvas.width;
  const ctx = out.getContext('2d')!;
  ctx.setTransform(0, -1, 1, 0, 0, canvas.width);
  ctx.drawImage(canvas, 0, 0);
  return out;
}

/** Export the drawn signature as a PNG cropped to its ink, so it scales well on the form. Up and down, it's cut so the pad's line
 * is SIGNATURE_LINE of the way down, as on the pad, for the form to put on its own line: past the ink above the line or below
 * it, whichever is short in that proportion. */
function trimmedPng(canvas: HTMLCanvasElement): Uint8Array {
  const ctx = canvas.getContext('2d')!;
  const { width, height } = canvas;
  const data = ctx.getImageData(0, 0, width, height).data;
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    if (data[(y * width + x) * 4 + 3] > 10) { if (x < minX) minX = x; if (x > maxX) maxX = x; if (y < minY) minY = y; if (y > maxY) maxY = y; }
  }
  const pad = 6;
  const out = document.createElement('canvas');
  if (maxX < 0) { out.width = 1; out.height = 1; } else {
    const line = height * SIGNATURE_LINE;
    const left = Math.max(0, minX - pad), across = Math.min(width, maxX + pad) - left;
    const above = Math.max(line - (minY - pad), (maxY + pad - line) * SIGNATURE_LINE / (1 - SIGNATURE_LINE), 1), tall = above / SIGNATURE_LINE;
    // A big screen's signature shrinks to the size the form takes.
    const { dimension, pixels } = PDF_SIGNATURE_LIMITS, shrink = Math.min(1, dimension / across, dimension / tall, Math.sqrt(pixels / (across * tall)));
    out.width = Math.max(1, Math.floor(across * shrink)); out.height = Math.max(1, Math.floor(tall * shrink));
    const ctx = out.getContext('2d')!;
    ctx.scale(shrink, shrink); ctx.drawImage(canvas, -left, above - line);
  }
  const b64 = out.toDataURL('image/png').split(',')[1];
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}
